import { createSelector } from '@reduxjs/toolkit';

import { UploadStatus, uploadArraySelector } from '@air/redux-uploader';

import { getClipFormFieldValue } from '../utils';
import {
  type ContentCollectionAssetAutoSubmitStatus,
  type ContentCollectionClip,
  type ContentCollectionClipWithUpload,
  type ContentCollectionState,
} from './types';

export const contentCollectionSelector = ({ contentCollection }: { contentCollection: ContentCollectionState }) =>
  contentCollection;

export const contentCollectionApplyAllSelector = createSelector(contentCollectionSelector, ({ applyAll }) => applyAll);

export const contentCollectionCustomFieldsSelector = createSelector(
  contentCollectionSelector,
  ({ customFields }) => customFields,
);

export const contentCollectionViewSelector = createSelector(contentCollectionSelector, ({ view }) => view);

export const contentCollectionFormSelector = createSelector(contentCollectionSelector, ({ form }) => {
  if (!form) {
    throw new Error('form is not defined!');
  }

  return form;
});

export const contentCollectionFormIdSelector = createSelector(contentCollectionFormSelector, (form) => form?.id);

export const contentCollectionFormTitleSelector = createSelector(
  contentCollectionFormSelector,
  (state) => state?.title,
);

export const contentCollectionFormDescriptionSelector = createSelector(
  contentCollectionFormSelector,
  (state) => state?.description,
);

export const contentCollectionTouchedSelector = createSelector(contentCollectionSelector, ({ touched }) => touched);

export const contentCollectionAttemptedSubmissionFormSelector = createSelector(
  contentCollectionSelector,
  ({ attemptedSubmission }) => attemptedSubmission,
);

export const contentCollectionSessionIdSelector = createSelector(
  contentCollectionSelector,
  ({ sessionId }) => sessionId,
);

export const contentCollectionClipsSelector = createSelector(
  [contentCollectionSelector, uploadArraySelector],
  ({ clips }, uploadsArray) => {
    const clipsWithUploads: ContentCollectionClipWithUpload[] = [];

    Object.values(clips).forEach((clip) => {
      const upload = uploadsArray.find((upload) => upload.clipId === clip.id);
      if (upload) {
        clipsWithUploads.push({ ...clip, upload });
      }
    });

    return clipsWithUploads;
  },
);

export const contentCollectionAllUploadsCompletedSelector = createSelector(
  [contentCollectionClipsSelector],
  (clips) => {
    return clips.length > 0 && clips.every((clip) => clip.upload.status === UploadStatus.completed);
  },
);

export const contentCollectionUploadedClipsSelector = createSelector([contentCollectionClipsSelector], (clips) => {
  return clips.filter((clip) => clip.upload.status === UploadStatus.completed);
});

export const contentCollectionSubmittedClipsSelector = createSelector(
  [contentCollectionSelector],
  (state) => state.submittedClips,
);

export const contentCollectionFieldsSelector = createSelector(contentCollectionFormSelector, (form) => {
  return form?.fields || [];
});

export const contentCollectionRequiredFieldsSelector = createSelector(
  contentCollectionFieldsSelector,
  (fields) => fields.filter(({ type, required }) => type !== 'title' && required) || [],
);

export const contentCollectionHasCustomFieldsSelector = createSelector(contentCollectionFormSelector, (form) => {
  if (!form || !form.fields.length) return false;

  return form.fields.some((field) => field.type === 'custom_field');
});

export const contentCollectionInvalidFieldsSelector = createSelector(
  [contentCollectionRequiredFieldsSelector, contentCollectionClipsSelector],
  (requiredFields, clips) =>
    requiredFields.filter((field) => clips.find((clip) => !getClipFormFieldValue(clip, field))),
);

export const contentCollectionClipsInvalidFieldsSelector = createSelector(
  [
    contentCollectionInvalidFieldsSelector,
    contentCollectionClipsSelector,
    (_st: unknown, ids: ContentCollectionClip['id'][]) => ids,
  ],
  (invalidFields, clips, clipIds) =>
    invalidFields.filter((field) =>
      clips.filter((clip) => clipIds.includes(clip.id)).find((clip) => !getClipFormFieldValue(clip, field)),
    ),
);

export const contentCollectionClipsInvalidFieldsLengthSelector = createSelector(
  [contentCollectionClipsInvalidFieldsSelector],
  (fields) => fields.length,
);

export const contentCollectionFormIsValidSelector = createSelector(
  [contentCollectionInvalidFieldsSelector],
  (fields) => !fields.length,
);

export const contentCollectionFormIsInvalidSelector = createSelector(
  [contentCollectionClipsInvalidFieldsLengthSelector],
  (invalidFields) => invalidFields > 0,
);

export const contentCollectionHasClipsSelector = createSelector(
  [contentCollectionClipsSelector],
  (clips) => !!clips.length,
);

export const contentCollectionSubmitterNameSelector = createSelector(
  contentCollectionSelector,
  ({ submitterName }) => submitterName,
);

export const contentCollectionFormSubmissionRequiredSelector = createSelector(
  contentCollectionSelector,
  ({ formSubmissionRequired }) => formSubmissionRequired,
);

export const contentCollectionAutoSubmitStatusByUploadIdSelector = createSelector(
  [contentCollectionSelector],
  ({ clips }) => {
    return Object.values(clips).reduce<Record<string, ContentCollectionAssetAutoSubmitStatus>>((acc, clip) => {
      if (clip.autoSubmit) {
        acc[clip.uploadId] = clip.autoSubmit;
      }
      return acc;
    }, {});
  },
);

export const contentCollectionHasFailedAutoSubmitSelector = createSelector([contentCollectionSelector], ({ clips }) =>
  Object.values(clips).some((clip) => clip.autoSubmit?.status === 'failure'),
);

export const contentCollectionPendingAutoSubmitCountSelector = createSelector(
  [contentCollectionClipsSelector],
  (clips) =>
    clips.filter((clip) => clip.autoSubmit?.status === 'pending' && clip.upload.status === UploadStatus.completed)
      .length,
);

export const contentCollectionHasAllClipsAutoSubmittedSelector = createSelector(
  [contentCollectionSelector],
  ({ clips }) =>
    Object.keys(clips).length > 0 && Object.values(clips).every((clip) => clip.autoSubmit?.status === 'success'),
);
